import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import { useSelectedOrder } from "@/context/selected-order-context";
import { useLocalization } from "@/context/localization-context";
import OrderDetail from "@/orders/order-detail";
import Orders from "@/orders/orders";
import SelectedProduct from "@/orders/selected-product";
import { useElementWidth } from "@/hooks/use-element-width";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

/** Page width from which the order list and the order detail sit side by side. */
const SPLIT_MIN = 1040;

export default function Page() {
  const { orderId, pizzaId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { setSelectedOrderId } = useSelectedOrder();
  const { setSelectedPizzaId, setApplyRoi } = usePizzaEvaluationContext();
  const [pageRef, width] = useElementWidth();
  const split = width >= SPLIT_MIN;

  useEffect(() => {
    setSelectedPizzaId(pizzaId || null);
  }, [pizzaId, setSelectedPizzaId]);

  useEffect(() => {
    setSelectedOrderId(orderId || null);
  }, [orderId, setSelectedOrderId]);

  useEffect(() => {
    setApplyRoi(true);
  }, []);

  // a pizza of the order: the detail takes the page, the order strip and the arrows browse the order
  if (pizzaId != null) {
    return (
      <div ref={pageRef} className="app-layout p-3 sm:p-4">
        <SelectedProduct navigation="order" swipeNavigation />
      </div>
    );
  }

  const list = (
    // from tablets up one screen tall: the toolbar and paging stay, the orders scroll inside
    <section
      className="flex flex-col rounded-xl border bg-card p-3 sm:p-4 md:h-[calc(100svh-5rem)] md:min-h-[30rem]"
      aria-label={t("common.orders")}
    >
      <h2 className="mb-3 text-base font-semibold tracking-tight sm:text-lg">{t("common.orders")}</h2>
      <Orders getLink={(path) => `/orders/${path}`} selectedId={orderId} />
    </section>
  );

  return (
    <div ref={pageRef} className="app-layout p-3 sm:p-4">
      {split ? (
        <div className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start gap-4">
          {list}
          <div className="h-[calc(100svh-5rem)] min-h-[30rem] overflow-y-auto rounded-xl">
            <OrderDetail />
          </div>
        </div>
      ) : orderId ? (
        // narrow screens drill down: the detail replaces the list, back returns to it
        <OrderDetail onBack={() => navigate("/orders")} />
      ) : (
        <div className={cn(width === 0 && "invisible")}>{list}</div>
      )}
    </div>
  );
}
